// src/components/SmartFlow/WorkflowsList.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkflowsStore } from "../../services/useWorkflowsStore";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";

const WorkflowsList = () => {
  const navigate = useNavigate();
  const { workflows, loading, error, fetchWorkflows, deleteWorkflow, executeWorkflow, activateWorkflow, deactivateWorkflow } = useWorkflowsStore();
  const [executingId, setExecutingId] = useState(null);

  useEffect(() => {
    fetchWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewWorkflow = () => {
    navigate("/smartflow/builder");
  };

  const handleEdit = (workflowId) => {
    navigate(`/smartflow/builder/${workflowId}`);
  };

  const handleRun = async (workflowId) => {
    setExecutingId(workflowId);
    try {
      await executeWorkflow(workflowId);
      alert("Workflow executed successfully!");
    } catch (error) {
      alert(`Failed to execute workflow: ${error.message}`);
    } finally {
      setExecutingId(null);
    }
  };

  const handleDelete = async (workflowId, workflowName) => {
    if (window.confirm(`Are you sure you want to delete workflow "${workflowName}"?`)) {
      try {
        await deleteWorkflow(workflowId);
      } catch (error) {
        alert(`Failed to delete workflow: ${error.message}`);
      }
    }
  };

  const handleToggleActive = async (workflow) => {
    try {
      if (workflow.is_active) {
        await deactivateWorkflow(workflow.id);
      } else {
        await activateWorkflow(workflow.id);
      }
    } catch (error) {
      alert(`Failed to ${workflow.is_active ? 'deactivate' : 'activate'} workflow: ${error.message}`);
    }
  };

  const getTriggerTypeLabel = (triggerType) => {
    const labels = {
      MANUAL: "Manual",
      CRM_LEAD_CREATED: "Lead Created",
      CRM_STAGE_CHANGED: "Stage Changed",
      SCHEDULED: "Scheduled",
      WEBHOOK: "Webhook",
    };
    return labels[triggerType] || triggerType;
  };

  if (loading && workflows.length === 0) {
    return (
      < FullScreenLoader message="Carregando Fluxos Inteligentes..." />
    );
  }

  if (error && workflows.length === 0) {
    return (
      <div style={{ padding: "20px" }} role="alert" aria-live="assertive">
        <div
          style={{
            background: "#f8d7da",
            border: "1px solid #f5c6cb",
            color: "#721c24",
            padding: "15px",
            borderRadius: "6px",
            marginBottom: "15px",
          }}
        >
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={fetchWorkflows}
          style={{
            padding: "8px 16px",
            background: "#3498db",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
          aria-label="Retry loading workflows"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ margin: "0 0 8px 0", fontSize: "24px" }}>Smart Flows</h2>
          <p style={{ margin: 0, color: "#7f8c8d", fontSize: "14px" }}>
            Crie e Gerencie seus Fluxos Inteligentes aqui!
          </p>
        </div>
        <button
          onClick={handleNewWorkflow}
          style={{
            padding: "10px 20px",
            background: "#3498db",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          + Criar Automação
        </button>
      </div>

      {/* Workflows Table */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Name</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Trigger Type</th>
              <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Created</th>
              <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {workflows.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: "60px 40px", textAlign: "center" }}>
                  <div style={{ maxWidth: "400px", margin: "0 auto" }}>
                    <div style={{ fontSize: "48px", marginBottom: "15px" }}>🔄</div>
                    <h3 style={{ margin: "0 0 10px 0", fontSize: "18px", color: "#2c3e50" }}>
                      No workflows yet
                    </h3>
                    <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#7f8c8d", lineHeight: "1.6" }}>
                      Create automated workflows to streamline your processes. Connect triggers, actions, and conditions to build powerful automations.
                    </p>
                    <button
                      onClick={handleNewWorkflow}
                      style={{
                        padding: "10px 24px",
                        background: "#3498db",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                      aria-label="Create your first workflow"
                    >
                      Crie seu primeiro Fluxo Inteligente
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              workflows.map((workflow) => (
                <tr key={workflow.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "12px" }}>
                    <div style={{ fontWeight: "500", fontSize: "14px" }}>{workflow.name}</div>
                    {workflow.description && (
                      <div style={{ fontSize: "12px", color: "#7f8c8d", marginTop: "4px" }}>
                        {workflow.description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px", fontSize: "14px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        background: "#e3f2fd",
                        color: "#1976d2",
                      }}
                    >
                      {getTriggerTypeLabel(workflow.trigger_type)}
                    </span>
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <button
                      onClick={() => handleToggleActive(workflow)}
                      style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        border: "none",
                        fontSize: "12px",
                        fontWeight: "500",
                        cursor: "pointer",
                        background: workflow.is_active ? "#d4edda" : "#f8d7da",
                        color: workflow.is_active ? "#155724" : "#721c24",
                      }}
                    >
                      {workflow.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td style={{ padding: "12px", fontSize: "14px", color: "#7f8c8d" }}>
                    {new Date(workflow.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                      <button
                        onClick={() => handleEdit(workflow.id)}
                        style={{
                          padding: "6px 12px",
                          background: "#3498db",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                        title="Edit"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleRun(workflow.id)}
                        disabled={executingId === workflow.id}
                        style={{
                          padding: "6px 12px",
                          background: executingId === workflow.id ? "#95a5a6" : "#27ae60",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: executingId === workflow.id ? "not-allowed" : "pointer",
                          fontSize: "12px",
                        }}
                        title="Run"
                      >
                        {executingId === workflow.id ? "Executando..." : "Executar"}
                      </button>
                      <button
                        onClick={() => handleDelete(workflow.id, workflow.name)}
                        style={{
                          padding: "6px 12px",
                          background: "#e74c3c",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                        title="Delete"
                      >
                        Deletar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default WorkflowsList;
